import { tagCleanser } from '@ups/xplat/utils';
import type {
  IDynamicContainerMetadata,
  IDynamicPageOptions,
  IDynamicResponseBody,
} from '../models/dynamic.interface';

export interface IDynamicAnsweredContainerSource {
  dynamicContainerList?: Array<IDynamicContainerMetadata>;
  dynamicContainerListAll?: Array<IDynamicContainerMetadata>;
  loadAllVersionContainerList(): Promise<Array<IDynamicContainerMetadata>>;
  loadContainerResponse(
    dynamicResponseId: string
  ): Promise<IDynamicResponseBody>;
}

/**
 * True when the page shows a saved response that must render on the version it was
 * answered on. Reclassification is excluded: it moves the answers onto the current
 * published form by control name, so it keeps the name match.
 */
export function dynamicIsViewingSavedResponse(
  options?: IDynamicPageOptions
): boolean {
  return !!options?.dynamicResponseId && !options.isReclassification;
}

/**
 * Resolves the container version a saved response was answered on (#31540).
 * Answers are keyed by dynamicControlId, and every form version has its own control
 * ids, so rendering a response on the current published version leaves it blank.
 * Web callers pass the answered dynamicContainerId; mobile and deep-link callers pass
 * only the response id, so the id is then read from the response itself.
 * Resolves undefined when the version cannot be found, so the caller can fall back
 * to the published form by name.
 */
export async function dynamicResolveAnsweredContainer(
  options: IDynamicPageOptions,
  source: IDynamicAnsweredContainerSource
): Promise<IDynamicContainerMetadata | undefined> {
  if (!dynamicIsViewingSavedResponse(options) || !options.dynamicResponseId) {
    return undefined;
  }
  const answeredId =
    options.dynamicContainerId ||
    options.dynamicResponseBody?.dynamicContainerId ||
    (await source.loadContainerResponse(options.dynamicResponseId))
      ?.dynamicContainerId;
  if (!answeredId) {
    return undefined;
  }
  const isAnswered = (c: IDynamicContainerMetadata) =>
    (c.dynamicContainerId || '').toLowerCase() === answeredId.toLowerCase();
  // published list first: a response on the current version needs no extra fetch
  return (
    source.dynamicContainerList?.find(isAnswered) ??
    (await source.loadAllVersionContainerList()).find(isAnswered)
  );
}

/**
 * The container a dynamic page renders: the answered version for a saved response,
 * otherwise the published form by name, then any version by the passed container id.
 * Resolves undefined when nothing matches; the page then alerts not-found.
 */
export async function dynamicResolvePageContainer(
  options: IDynamicPageOptions,
  source: IDynamicAnsweredContainerSource
): Promise<IDynamicContainerMetadata | undefined> {
  const answered = await dynamicResolveAnsweredContainer(options, source);
  if (answered) {
    return answered;
  }
  const byName = (c: IDynamicContainerMetadata) =>
    tagCleanser(c.dynamicContainerName || '') === options.name;
  const container =
    source.dynamicContainerList?.find(byName) ||
    // unpublished form but viewing a response
    (options.dynamicResponseId
      ? source.dynamicContainerListAll?.find(byName)
      : undefined);
  if (container || !options.dynamicContainerId) {
    return container;
  }
  return (await source.loadAllVersionContainerList()).find(
    (c) => c.dynamicContainerId === options.dynamicContainerId
  );
}
